import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Radio } from 'antd';
import { ExclamationCircleOutlined, CloudOutlined, FileOutlined } from '@ant-design/icons';
import { padServices } from '../../api';
import { adjustEditorHeightWithSignatures, removeOfflineData, safeParseEditorState, showPagebreak } from '../../utils/helper';
import { useParams } from 'react-router-dom';
import './style.css';
import PreviewVersionModal from './previewVersionModal';
import { useDispatch, useSelector } from 'react-redux';
import { $generateHtmlFromNodes } from '@lexical/html';
import { insertEditorInstancesDelta, loadSignatures, setEditorSummary, setIsConflictModalVisible, setIsExternalAllow } from '../../app/EditorSlice';
import { emitUpdateAll } from '../../Socket';

const { Title, Text } = Typography;

const DocumentDataConflictModal = () => {

    const { id } = useParams();
    const dispatch = useDispatch();
    const [localData, setLocalData] = useState(null);
    const [serverPadData, setServerPadData] = useState(null);
    const [selectedRadioOption, setSelectedRadioOption] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const editor = useSelector((state) => state.editor.lexicalEditor);
    const serverData = useSelector((state) => state.editor.serverData);
    const conflictPreviewVerion = useSelector((state) => state.editor.conflictPreviewVerion);
    const isConflictModalVisible = useSelector((state) => state.editor.isConflictModalVisible);

    useEffect(() => {
        if (!isConflictModalVisible) return;

        const fetchData = async () => {
            try {
                const offlineData = JSON.parse(localStorage.getItem("offlineMilepadData") || "{}");
                const localContent = offlineData[id]?.data || null;
                setLocalData(localContent);
                setServerPadData(serverData);
            } catch (error) {
                console.error('Error fetching conflict data:', error);
                dispatch(setIsConflictModalVisible(false));
            }
        };
        fetchData();
    }, [id, isConflictModalVisible, serverData]);

    const loadDataIntoEditor = (data, isKeepOffline = false) => {
        if (!data?.milepadData || !data?.summary || !data?.Signatures) {
            return;
        }
        const initialState = safeParseEditorState(editor, data?.milepadData);
        const signatures = data?.Signatures;
        editor.setEditorState(initialState);
        // previousEditorStateRef.current = JSON.stringify(initialState.toJSON());
        dispatch(loadSignatures(signatures));
        adjustEditorHeightWithSignatures(signatures);
        showPagebreak();
        const htmlContent = initialState.read(() => $generateHtmlFromNodes(editor, null));
        if (htmlContent) {
            dispatch(insertEditorInstancesDelta(htmlContent));
        }
        dispatch(setIsExternalAllow(data?.isExternal || false));
        dispatch(setEditorSummary(data?.summary));
        isKeepOffline && emitUpdateAll({ roomID: id, data: { message: "offline version loaded" } });
    };

    const handleKeepOffline = async () => {
        if (!localData) {
            console.error('Local data is not available.');
            onClose();
            return;
        }

        try {
            const dataToLoad = {
                ...serverData,
                summary: localData.summary,
                milepadData: localData.milepadData,
            };
            loadDataIntoEditor(dataToLoad, true);
            await padServices.saveToDatabase(id, localData.milepadData, localData.summary, dispatch);
            removeOfflineData(id);
        } catch (error) {
            console.error('Failed to keep offline data:', error);
        } finally {
            onClose();
        }
    };

    const handleUseServer = () => {
        if (!serverPadData) {
            console.error('No server data available');
            onClose();
            return;
        }

        try {
            loadDataIntoEditor(serverPadData);
        } catch (error) {
            console.error('Failed to load server data:', error);
        } finally {
            onClose();
        }
    };

    const onClose = () => {
        dispatch(setIsConflictModalVisible(false));
    };

    const handleVersionClick = (version) => {
        setPreviewVisible(true);
        setSelectedRadioOption(version);
    };

    if (conflictPreviewVerion) return null;

    return (
        <div>
            <Modal
                visible={isConflictModalVisible}
                onCancel={() => { }}
                closable={false}
                maskClosable={false}
                footer={null}
                width={480}
                centered
                className="document-conflict-modal"
                closeIcon={<span className="close-icon">×</span>}
            >
                <div className="modal-content">
                    <div className="warning-icon-container">
                        <ExclamationCircleOutlined className="warning-icon" />
                    </div>
                    <Title level={3} className="modal-title">
                        Document Version Conflict
                    </Title>
                    <Text className="modal-description">
                        A newer version of this document is available on the server. Would you like to retain your offline changes or use the server version?
                    </Text>
                    <div className="version-options">
                        <Radio.Group
                            value={selectedRadioOption}
                            // onChange={handleRadioChange}
                            className="version-radio-group"
                        >
                            <div className={`version-option ${selectedRadioOption === 'offline' ? 'selected' : ''}`}>
                                <div>
                                    <Radio value="offline" className="version-radio" onClick={() => handleVersionClick('offline')}>
                                        <div className="version-content">
                                            <FileOutlined className="version-icon offline-icon" />
                                            <div className="version-text">
                                                <div className="version-title">Offline Version</div>
                                                <div className="version-subtitle">Your local changes</div>
                                            </div>
                                        </div>
                                    </Radio>
                                </div>
                            </div>

                            <div className={`version-option ${selectedRadioOption === 'server' ? 'selected' : ''}`}>
                                <div>
                                    <Radio value="server" className="version-radio" onClick={() => handleVersionClick('server')}>
                                        <div className="version-content">
                                            <CloudOutlined className="version-icon server-icon" />
                                            <div className="version-text">
                                                <div className="version-title">Server Version</div>
                                                <div className="version-subtitle">Latest from server</div>
                                            </div>
                                        </div>
                                    </Radio>
                                </div>
                            </div>
                        </Radio.Group>
                    </div>
                    <div className="modal-actions">
                        <Button type="primary" size="large" className="keep-offline-btn" onClick={handleKeepOffline}>
                            <FileOutlined /> Keep Offline Changes
                        </Button>
                        <Button size="large" className="use-server-btn" onClick={handleUseServer}>
                            <CloudOutlined /> Use Server Version
                        </Button>
                    </div>
                </div>
            </Modal>
            {previewVisible && selectedRadioOption && (
                <PreviewVersionModal
                    version={selectedRadioOption}
                    isvisible={previewVisible}
                    setIsVisible={setPreviewVisible}
                />
            )}
        </div>
    );
};

export default DocumentDataConflictModal;